import { Component } from '@theme/component';

const mobileQuery = window.matchMedia('(max-width: 749px)');

/**
 * @typedef {object} Refs
 * @property {HTMLElement | HTMLElement[]} track
 * @property {HTMLElement} progress
 * @property {HTMLElement} indicator
 * @extends {Component<Refs>}
 */
class PrettyPortalCarouselComponent extends Component {
  #controller = new AbortController();
  #frameId = 0;
  #resizeObserver = /** @type {ResizeObserver | null} */ (null);
  #scheduleFrameId = 0;
  #timeoutIds = /** @type {number[]} */ ([]);

  connectedCallback() {
    super.connectedCallback();
    this.#controller = new AbortController();
    this.#bindCarousel();
    this.refresh();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#cleanup();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#cleanup();
    this.#controller = new AbortController();
    this.#bindCarousel();
    this.refresh();
  }

  get #tracks() {
    const tracks = this.refs.track;

    if (Array.isArray(tracks)) {
      return tracks.filter((track) => track instanceof HTMLElement);
    }

    return tracks instanceof HTMLElement ? [tracks] : [];
  }

  get #progress() {
    return this.refs.progress instanceof HTMLElement ? this.refs.progress : null;
  }

  get #indicator() {
    return this.refs.indicator instanceof HTMLElement ? this.refs.indicator : null;
  }

  get #activeTrack() {
    const activePanelSelector = this.getAttribute('active-panel-selector');

    if (activePanelSelector) {
      const activePanel = this.querySelector(activePanelSelector);
      const activeTrack = activePanel?.querySelector('[ref="track"], [ref="track[]"]');
      if (activeTrack instanceof HTMLElement) return activeTrack;
    }

    return this.#tracks[0] || null;
  }

  get #isMobileOnly() {
    return this.hasAttribute('mobile-only');
  }

  #bindCarousel() {
    const { signal } = this.#controller;
    const progress = this.#progress;
    const indicator = this.#indicator;
    const tracks = this.#tracks;
    if (!progress || !indicator || !tracks.length) return;

    tracks.forEach((track) => {
      track.addEventListener('scroll', this.sync, { passive: true, signal });
      track.querySelectorAll('img').forEach((image) => {
        if (image.complete) return;
        image.addEventListener('load', this.refresh, { once: true, signal });
      });
    });

    progress.addEventListener('click', this.#handleProgressClick, { signal });
    progress.addEventListener('keydown', this.#handleProgressKeydown, { signal });
    this.querySelectorAll('[data-pp-carousel-prev]').forEach((button) => {
      button.addEventListener('click', () => this.#scrollActiveTrack(-1), { signal });
    });
    this.querySelectorAll('[data-pp-carousel-next]').forEach((button) => {
      button.addEventListener('click', () => this.#scrollActiveTrack(1), { signal });
    });
    this.addEventListener('pp:carousel:refresh', this.refresh, { signal });

    if ('ResizeObserver' in window) {
      this.#resizeObserver = new ResizeObserver(this.refresh);
      this.#resizeObserver.observe(this);
      tracks.forEach((track) => this.#resizeObserver?.observe(track));
    }

    window.addEventListener('resize', this.refresh, { signal });
    window.addEventListener('load', this.refresh, { once: true, signal });

    if (typeof mobileQuery.addEventListener === 'function') {
      mobileQuery.addEventListener('change', this.refresh, { signal });
    } else if (typeof mobileQuery.addListener === 'function') {
      mobileQuery.addListener(this.refresh);
    }
  }

  #cleanup() {
    this.#controller.abort();
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    window.cancelAnimationFrame(this.#frameId);
    window.cancelAnimationFrame(this.#scheduleFrameId);
    this.#timeoutIds.forEach((timeoutId) => window.clearTimeout(timeoutId));
    this.#timeoutIds.length = 0;

    if (typeof mobileQuery.removeListener === 'function') {
      mobileQuery.removeListener(this.refresh);
    }
  }

  #resetProgress() {
    const progress = this.#progress;
    const indicator = this.#indicator;
    if (!progress || !indicator) return;

    progress.hidden = true;
    progress.setAttribute('aria-valuenow', '0');
    indicator.style.left = '';
    indicator.style.width = '';
  }

  sync = () => {
    const track = this.#activeTrack;
    const progress = this.#progress;
    const indicator = this.#indicator;
    if (!track || !progress || !indicator) return false;

    window.cancelAnimationFrame(this.#frameId);
    this.#frameId = window.requestAnimationFrame(() => {
      const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);

      if ((this.#isMobileOnly && !mobileQuery.matches) || maxScroll <= 1 || track.scrollWidth <= 0) {
        this.#resetProgress();
        this.#syncNavigation(false);
        this.dispatchEvent(new CustomEvent('pp:carousel:sync', { detail: { track, hasOverflow: false } }));
        return;
      }

      const scrollRatio = Math.min(1, Math.max(0, track.scrollLeft / maxScroll));
      const visibleRatio = Math.min(1, Math.max(0, track.clientWidth / track.scrollWidth));
      const thumbLeft = scrollRatio * (1 - visibleRatio) * 100;

      progress.hidden = false;
      progress.setAttribute('aria-valuenow', String(Math.round(scrollRatio * 100)));
      indicator.style.left = `${thumbLeft}%`;
      indicator.style.width = `${visibleRatio * 100}%`;
      this.#syncNavigation(true, scrollRatio);
      this.dispatchEvent(new CustomEvent('pp:carousel:sync', { detail: { track, hasOverflow: true } }));
    });

    return true;
  };

  #syncNavigation(hasOverflow, scrollRatio = 0) {
    const previousButtons = this.querySelectorAll('[data-pp-carousel-prev]');
    const nextButtons = this.querySelectorAll('[data-pp-carousel-next]');
    const disablePrevious = !hasOverflow || scrollRatio <= 0.01;
    const disableNext = !hasOverflow || scrollRatio >= 0.99;

    previousButtons.forEach((button) => {
      if (button instanceof HTMLButtonElement) button.disabled = disablePrevious;
    });
    nextButtons.forEach((button) => {
      if (button instanceof HTMLButtonElement) button.disabled = disableNext;
    });
  }

  #scrollActiveTrack(direction) {
    const track = this.#activeTrack;
    if (!track) return;

    track.scrollBy({
      left: track.clientWidth * 0.82 * direction,
      behavior: 'smooth',
    });
  }

  refresh = () => {
    this.sync();
    window.cancelAnimationFrame(this.#scheduleFrameId);
    this.#scheduleFrameId = window.requestAnimationFrame(this.sync);
    this.#timeoutIds.forEach((timeoutId) => window.clearTimeout(timeoutId));
    this.#timeoutIds.length = 0;
    this.#timeoutIds.push(window.setTimeout(this.sync, 150));
    this.#timeoutIds.push(window.setTimeout(this.sync, 500));
  };

  #handleProgressClick = (event) => {
    const track = this.#activeTrack;
    const progress = this.#progress;
    if (!track || !progress) return;

    const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
    const rect = progress.getBoundingClientRect();
    if (maxScroll <= 1 || rect.width <= 0 || track.scrollWidth <= 0) return;

    const clickRatio = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
    const visibleRatio = Math.min(1, Math.max(0, track.clientWidth / track.scrollWidth));
    const scrollRatio =
      visibleRatio >= 1 ? 0 : Math.min(1, Math.max(0, (clickRatio - visibleRatio / 2) / (1 - visibleRatio)));

    track.scrollTo({ left: scrollRatio * maxScroll, behavior: 'smooth' });
  };

  #handleProgressKeydown = (event) => {
    const track = this.#activeTrack;
    if (!track) return;

    const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
    if (maxScroll <= 1) return;

    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.preventDefault();
      track.scrollBy({
        left: event.key === 'ArrowRight' ? track.clientWidth * 0.75 : track.clientWidth * -0.75,
        behavior: 'smooth',
      });
    } else if (event.key === 'Home') {
      event.preventDefault();
      track.scrollTo({ left: 0, behavior: 'smooth' });
    } else if (event.key === 'End') {
      event.preventDefault();
      track.scrollTo({ left: maxScroll, behavior: 'smooth' });
    }
  };
}

if (!customElements.get('pretty-portal-carousel-component')) {
  customElements.define('pretty-portal-carousel-component', PrettyPortalCarouselComponent);
}
